import { Fragment, useRef } from 'react';
import type { ClipboardEvent, KeyboardEvent } from 'react';

const ALNUM = /[a-z0-9]/i;

// The emailed code is alphabetic, but accept digits too so the box count stays
// right if the code format is ever switched to numeric.
const sanitize = (raw: string, max: number): string =>
    (raw ?? '')
        .split('')
        .filter((char) => ALNUM.test(char))
        .join('')
        .toUpperCase()
        .slice(0, max);

type Props = {
    value: string;
    onChange: (value: string) => void;
    /** Number of boxes. Wave's login code is eight characters (`XXXX-XXXX`). */
    length?: number;
    /** Draw a divider after every `groupSize` boxes; 0 disables the divider. */
    groupSize?: number;
    invalid?: boolean;
    disabled?: boolean;
    autoFocus?: boolean;
    ariaLabel?: string;
};

/**
 * Segmented one-box-per-character input for the emailed sign-in code. Typing
 * auto-advances, backspace steps back, arrows move between boxes, and pasting a
 * whole code (with or without the dash) fills every box. The joined value flows
 * out through `onChange`; the backend strips punctuation and is case-insensitive
 * (see allauth compare_user_code), so the bare characters are enough.
 */
const OtpCodeInput = ({
    value,
    onChange,
    length = 8,
    groupSize = 4,
    invalid = false,
    disabled = false,
    autoFocus = false,
    ariaLabel = 'Sign-in code',
}: Props) => {
    const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
    const chars = sanitize(value, length);
    const slots = Array.from({ length }, (_, index) => chars[index] ?? '');

    const focusBox = (index: number) => {
        const clamped = Math.max(0, Math.min(length - 1, index));
        const box = inputsRef.current[clamped];
        box?.focus();
        box?.select();
    };

    const commit = (nextSlots: string[]) => onChange(nextSlots.join(''));

    const handleChange = (index: number, raw: string) => {
        const char = sanitize(raw, 1);
        const nextSlots = [...slots];
        nextSlots[index] = char;
        commit(nextSlots);
        if (char) {
            focusBox(index + 1);
        }
    };

    const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Backspace') {
            event.preventDefault();
            const nextSlots = [...slots];
            if (nextSlots[index]) {
                nextSlots[index] = '';
                commit(nextSlots);
            } else if (index > 0) {
                nextSlots[index - 1] = '';
                commit(nextSlots);
                focusBox(index - 1);
            }
            return;
        }
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            focusBox(index - 1);
        }
        if (event.key === 'ArrowRight') {
            event.preventDefault();
            focusBox(index + 1);
        }
    };

    const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault();
        const pasted = sanitize(event.clipboardData.getData('text'), length);
        if (!pasted) {
            return;
        }
        onChange(pasted);
        focusBox(pasted.length >= length ? length - 1 : pasted.length);
    };

    return (
        <div className="wm-otp" role="group" aria-label={ariaLabel}>
            {slots.map((char, index) => (
                <Fragment key={index}>
                    <input
                        ref={(element) => {
                            inputsRef.current[index] = element;
                        }}
                        className={`wm-otp__box ${invalid ? 'wm-otp__box--invalid' : ''}`}
                        type="text"
                        inputMode="text"
                        size={1}
                        autoCapitalize="characters"
                        autoComplete={index === 0 ? 'one-time-code' : 'off'}
                        autoFocus={autoFocus && index === 0}
                        disabled={disabled}
                        maxLength={1}
                        aria-label={`Character ${index + 1}`}
                        value={char}
                        onChange={(event) => handleChange(index, event.target.value)}
                        onKeyDown={(event) => handleKeyDown(index, event)}
                        onPaste={handlePaste}
                        onFocus={(event) => event.target.select()}
                    />
                    {groupSize > 0 && (index + 1) % groupSize === 0 && index < length - 1 ? (
                        <span className="wm-otp__sep" aria-hidden>
                            -
                        </span>
                    ) : null}
                </Fragment>
            ))}
        </div>
    );
};

export default OtpCodeInput;
